import { createAsyncThunk } from '@reduxjs/toolkit';
import { BillingCycle } from '../../types/billing';
import {
  getAuthSessionUser,
  getCurrentUser,
  loginUser,
  loginUserWithCode,
  logoutUser,
  registerUser,
  setupAccount as setupAccountService,
  signInWithGoogle,
  verifyEmail as verifyEmailService,
  registerCurrentDevice,
} from '../../services/authService';
import { fetchCurrentUserAccessControlCodes } from '../../services/accessControlService';
import { isElectron } from '../../util';
import { supabase } from '../../services/supabase';
import {
  consumeUserInitiatedSignOut,
  markUserInitiatedSignOut,
} from '../../lib/auth/signOutIntent';
import { resetOnboarding } from '../onboardingSlice';
import { fetchOutlets } from '../outlets';
import { ResolvedAuthUserPayload } from '../../types/auth';

/**
 * Thunk: Get current user session
 */
export const fetchCurrentUser = createAsyncThunk<ResolvedAuthUserPayload, void>(
  'auth/fetchCurrentUser',
  async (_, { dispatch }) => {
    const user = await getCurrentUser();
    const accessControlsResponse = user
      ? await fetchCurrentUserAccessControlCodes(user.id)
      : { success: true, data: [] as string[] };

    if (user && user.company) {
      dispatch(fetchOutlets(user.company.id));
    }

    return {
      user,
      accessControlCodes: accessControlsResponse.data || [],
    };
  },
);

/**
 * Thunk: Register a new user
 */
export const register = createAsyncThunk(
  'auth/register',
  async (
    formData: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    const response = await registerUser(formData);
    if (!response.success) {
      return rejectWithValue(response.error || 'Registration failed');
    }
    dispatch(fetchCurrentUser());
    return response;
  },
);

/**
 * Thunk: Verify email
 */
export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (
    { email, token }: { email: string; token: string },
    { rejectWithValue },
  ) => {
    const response = await verifyEmailService(email, token);
    if (!response.success) {
      return rejectWithValue(response.error || 'Verification failed');
    }
    return response.user;
  },
);

/**
 * Thunk: Setup account (Company, Outlet, Billing)
 */
export const setupAccount = createAsyncThunk(
  'auth/setupAccount',
  async (
    data: {
      businessName: string;
      businessType: string;
      billingCycle?: BillingCycle;
      countryId: number;
      package: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    const response = await setupAccountService(data);
    if (!response.success) {
      return rejectWithValue(response.error || 'Setup failed');
    }
    dispatch(fetchCurrentUser());
    return response;
  },
);

/**
 * Thunk: Login user
 */
export const login = createAsyncThunk<
  ResolvedAuthUserPayload,
  { email: string; password: string },
  { rejectValue: string }
>('auth/login', async ({ email, password }, { rejectWithValue }) => {
  const response = await loginUser(email, password);
  if (!response.success) {
    return rejectWithValue(response.error || 'Login failed');
  }

  // Register device if user belongs to an outlet
  if (response.user?.outlet_id) {
    try {
      await registerCurrentDevice(response.user.outlet_id);
    } catch (err) {
      console.error('[DeviceRegistration] Failed during login:', err);
    }
  }

  const accessControlsResponse = response.user
    ? await fetchCurrentUserAccessControlCodes(response.user.id)
    : { success: true, data: [] as string[] };

  return {
    user: response.user || null,
    accessControlCodes: accessControlsResponse.data || [],
  };
});

/**
 * Thunk: Login user using passcode
 */
export const loginWithCode = createAsyncThunk<
  ResolvedAuthUserPayload,
  { code: string },
  { rejectValue: string }
>('auth/loginWithCode', async ({ code }, { rejectWithValue }) => {
  // Fetch device details first to supply fingerprint
  let deviceFingerprint = 'unknown';
  try {
    const details = (await (window as any).electron.ipcRenderer.invoke(
      'system:getDeviceDetails',
    )) as any;
    if (details?.success && details.data) {
      deviceFingerprint = details.data.device_fingerprint;
    }
  } catch (err) {
    console.error(
      '[DeviceFingerprint] Failed to fetch device fingerprint in thunk:',
      err,
    );
  }

  const response = await loginUserWithCode(code, deviceFingerprint);
  if (!response.success) {
    return rejectWithValue(response.error || 'Login failed');
  }

  const accessControlsResponse = response.user
    ? await fetchCurrentUserAccessControlCodes(response.user.id)
    : { success: true, data: [] as string[] };

  return {
    user: response.user || null,
    accessControlCodes: accessControlsResponse.data || [],
  };
});

/**
 * Thunk: Sign in with Google OAuth.
 * Desktop: opens the system browser for OAuth, waits for the deep-link
 * callback, exchanges the auth code for a session, then resolves the user.
 * Web: triggers a full-page redirect; App.tsx onAuthStateChange handles the
 * rest, so this thunk resolves immediately after starting the redirect.
 */
export const loginWithGoogle = createAsyncThunk<
  ResolvedAuthUserPayload,
  void,
  { rejectValue: string }
>('auth/loginWithGoogle', async (_, { dispatch, rejectWithValue }) => {
  try {
    if (!isElectron()) {
      // Web: trigger OAuth redirect; App.tsx SIGNED_IN handler takes over.
      await signInWithGoogle();
      return { user: null, accessControlCodes: [] };
    }

    // Desktop: open browser, then wait for the deep-link callback URL.
    await signInWithGoogle();

    return await new Promise<ResolvedAuthUserPayload>((resolve, reject) => {
      let unsubscribe: (() => void) | null = null;

      // If the browser is closed, an error page is shown (e.g. redirect_uri_mismatch),
      // or the user simply walks away, the deep-link never arrives. Without a timeout
      // the thunk stays pending forever and the button stays disabled.
      const timeoutId = setTimeout(
        () => {
          if (unsubscribe) {
            unsubscribe();
            unsubscribe = null;
          }
          reject(
            new Error(
              'Google sign-in was cancelled or timed out. Please try again.',
            ),
          );
        },
        2 * 60 * 1000,
      ); // 2 minutes

      unsubscribe = (window as any).electron.auth.onGoogleCallback(
        async (callbackUrl: string) => {
          clearTimeout(timeoutId);
          if (unsubscribe) {
            unsubscribe();
            unsubscribe = null;
          }
          try {
            // Supabase uses implicit flow for deep-link redirects: tokens arrive in
            // the URL fragment (#access_token=...&refresh_token=...), not as a PKCE
            // ?code= param. Calling exchangeCodeForSession() would treat the whole
            // deep-link URL as the auth code and fail with "code verifier empty".
            const fragmentStr = callbackUrl.split('#')[1] || '';
            const fragment = new URLSearchParams(fragmentStr);
            const accessToken = fragment.get('access_token');
            const refreshToken = fragment.get('refresh_token');
            if (!accessToken || !refreshToken) {
              throw new Error('OAuth callback is missing required tokens');
            }
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (error) throw new Error(error.message);

            const user = await getCurrentUser();
            if (user) {
              const acResponse = await fetchCurrentUserAccessControlCodes(
                user.id,
              );
              if (user.company?.id) dispatch(fetchOutlets(user.company.id));
              resolve({ user, accessControlCodes: acResponse.data || [] });
            } else {
              // New Google user: Supabase auth session exists but no app profile yet.
              // Pass back pendingGoogleUser in the payload; authSlice.fulfilled handles it.
              const authUser = await getAuthSessionUser();
              resolve({
                user: null,
                accessControlCodes: [],
                pendingGoogleUser: authUser
                  ? {
                      id: authUser.id,
                      email: authUser.email || '',
                      name:
                        authUser.user_metadata?.full_name ||
                        authUser.user_metadata?.name ||
                        '',
                    }
                  : null,
              });
            }
          } catch (e) {
            reject(e);
          }
        },
      );
    });
  } catch (e) {
    return rejectWithValue(
      e instanceof Error ? e.message : 'Google sign-in failed',
    );
  }
});

/**
 * Thunk: Logout user and reset all app state
 *
 * Always succeeds immediately, online or offline. Local PowerSync data is
 * never cleared on logout (see database.ts), so any pending unsynced
 * changes remain safely queued and upload once connectivity returns,
 * regardless of who's signed in — sign-out must never block a shift
 * hand-off between staff sharing the same terminal.
 */
export const logout = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/logout',
  async (_, { dispatch }) => {
    // Tell App.tsx's SIGNED_OUT handler this was deliberate, so it doesn't
    // show the "session expired" toast. Cleared afterwards in case signOut
    // didn't emit SIGNED_OUT (e.g. no session), so a later genuine expiry
    // isn't silenced.
    markUserInitiatedSignOut();
    try {
      await logoutUser();
    } finally {
      consumeUserInitiatedSignOut();
    }
    // Reset other slices
    dispatch(resetOnboarding());
    // Note: auth state is reset via extraReducers in authSlice
    return undefined;
  },
);
